import React from "react";
import { Helmet } from "react-helmet-async";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
  noIndex?: boolean;
  schemaData?: object;
}

const DEFAULT_TITLE =
  "Myhu - Environmental Sustainability App | Community-Driven Eco-Monitoring";
const DEFAULT_DESCRIPTION =
  "Discover Myhu, the all-in-one environmental sustainability app that tracks your eco-impact and connects you to Afro-ecological wisdom.";
const DEFAULT_KEYWORDS = [
  "environmental sustainability app",
  "eco-monitoring platform",
  "community environmental knowledge",
  "carbon footprint tracker",
  "community-driven conservation technology",
  "real-time biodiversity monitoring",
  "sustainable lifestyle planner",
  "environmental impact analytics",
];

const SEO: React.FC<SEOProps> = ({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords = DEFAULT_KEYWORDS,
  canonicalUrl,
  ogImage = "/og-image.png",
  ogType = "website",
  twitterCard = "summary_large_image",
  noIndex = false,
  schemaData,
}) => {
  const fullTitle = title ? `${title} | Myhu` : DEFAULT_TITLE;
  const fullCanonicalUrl =
    canonicalUrl || (typeof window !== "undefined" ? window.location.href : "");

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(", ")} />
      <meta name="author" content="Myhu" />
      <meta
        name="robots"
        content={noIndex ? "noindex,nofollow" : "index,follow"}
      />
      <meta name="language" content="English" />

      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={fullCanonicalUrl} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Myhu" />

      {/* Twitter */}
      <meta property="twitter:card" content={twitterCard} />
      <meta property="twitter:url" content={fullCanonicalUrl} />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={ogImage} />

      {/* Additional Meta Tags */}
      <meta name="theme-color" content="#22c55e" />
      <meta name="msapplication-TileColor" content="#22c55e" />

      {/* Schema.org JSON-LD */}
      {schemaData && (
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      )}

      {/* Default Organization Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Myhu",
          url: "https://myhu.world",
          logo: "https://myhu.world/favicon.ico",
          description:
            "Environmental sustainability platform connecting users to Afro-ecological wisdom and community-driven ecological monitoring",
          sameAs: ["https://github.com/Awphel/ecolens"],
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "Support",
            url: "https://myhu.world",
          },
        })}
      </script>
    </Helmet>
  );
};

export default SEO;
